import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { Logo } from '../components/Logo';

// One answer to "forgot your password", whatever the truth is. Saying "no such account" here would
// turn this form into a way of asking whether a named teacher works at a school — so the wording
// below is returned for a real address, a stranger's address and a typo alike, and every error from
// Auth is swallowed on purpose.
const RESET_ANSWER = 'If that address has an account, a reset link is on its way. Check the spam folder if it does not arrive.';

function Gatekeeper() {
  const [searchParams] = useSearchParams();
  const isSignup = searchParams.get('signup') === 'true';
  const navigate = useNavigate();
  const { currentUser, loading: authLoading, login, signup } = useAuth();

  // Redirect logged-in users to dashboard
  useEffect(() => {
    if (!authLoading && currentUser) {
      navigate('/desk', { replace: true });
    }
  }, [currentUser, authLoading, navigate]);
  
  const [mode, setMode] = useState(isSignup ? 'signup' : 'login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    schoolName: '',
    discountCode: '',
    accessCode: '',
    selectedPlan: 'starter' // Default to starter plan
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Access codes are now validated server-side via Cloud Function
  // This prevents exposure in the client-side bundle

  // Subscription plans (coming soon - currently disabled)
  const subscriptionPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$9.99/month',
      description: 'Perfect for small libraries',
      features: [
        'Up to 500 books',
        'Basic content filtering',
        'Standard support',
        'LibraryWorld integration'
      ],
      disabled: true
    },
    {
      id: 'professional',
      name: 'Professional', 
      price: '$19.99/month',
      description: 'For growing collections',
      features: [
        'Up to 2,000 books',
        'Advanced content filtering',
        'Priority support',
        'Custom barcode ranges',
        'Bulk operations'
      ],
      disabled: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$39.99/month', 
      description: 'For large institutions',
      features: [
        'Unlimited books',
        'Custom filtering rules',
        'Dedicated support',
        'Multi-library management',
        'Advanced reporting',
        'API access'
      ],
      disabled: true
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (mode === 'reset') {
      // Answer AT ONCE, and never wait on the send. Awaiting it leaked the very thing this form
      // must not tell you: an address with an account behind it takes seconds (Auth composes and
      // posts a mail), one without comes back in milliseconds with user-not-found — so the answer
      // arrived at two visibly different speeds and the delay was the answer. Measured on staging,
      // 27 Sept 2026. Detached, every address is answered in the same instant with the same words.
      sendPasswordResetEmail(auth, formData.email.trim()).catch(() => { /* never say which */ });
      setResetSent(true);
      setLoading(false);
      return;
    }

    try {
      if (mode === 'signup') {
        // Access codes are validated server-side in AuthContext.signup()
        await signup(formData);
        // After successful signup, redirect to onboarding
        navigate('/onboarding');
      } else {
        await login(formData.email, formData.password);
        navigate('/desk');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const goTo = (next) => { setMode(next); setError(''); setResetSent(false); };
  const heading = mode === 'signup' ? 'Request access' : mode === 'reset' ? 'Reset your password' : 'Sign in';
  const submitLabel = mode === 'reset' ? 'Send the link' : heading;
  const field = 'w-full text-input px-3 py-2 border border-hairline rounded-[2px] focus:ring-2 focus:ring-petrol focus:border-transparent';
  const label = 'block text-label text-ink mb-1';

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col">
      <header className="bg-petrol text-white">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link to="/"><Logo size={32} showText /></Link>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-display mb-2">{heading}</h1>
          <p className="text-body text-muted mb-8">
            {mode === 'signup'
              ? 'Heimdl is in private beta, so new libraries are set up by hand — tell us about yours.'
              : mode === 'reset'
                ? 'Give us the address you sign in with and we will send a link to set a new password.'
                : 'Sign in to your library.'}
          </p>

          {resetSent ? (
            <>
              <div className="border-2 border-lemon rounded-[2px] px-3 py-3">
                <p className="text-body text-ink">{RESET_ANSWER}</p>
              </div>
              <button type="button" onClick={() => goTo('login')} className="text-body text-petrol hover:underline mt-6">
                Back to sign in
              </button>
            </>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'signup' && (
              <>
                <div>
                  <label className={label} htmlFor="accessCode">Access code</label>
                  <input
                    id="accessCode" type="password" required className={field}
                    value={formData.accessCode}
                    onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
                  />
                  <p className="text-label text-muted mt-1">
                    Heimdl is in private beta. Ask us for a code and we will set your library up with you.
                  </p>
                </div>
                <div>
                  <label className={label} htmlFor="schoolName">School</label>
                  <input
                    id="schoolName" type="text" required className={field}
                    placeholder="Holy Family Catholic School"
                    value={formData.schoolName}
                    onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                  />
                </div>
              </>
            )}

            <div>
              <label className={label} htmlFor="email">Email</label>
              <input
                id="email" type="email" required className={field} autoComplete="email"
                placeholder="librarian@school.org"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            {mode !== 'reset' && (
              <div>
                <label className={label} htmlFor="password">Password</label>
                <input
                  id="password" type="password" required className={field}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                {mode === 'login' && (
                  <button type="button" onClick={() => goTo('reset')} className="text-label text-petrol hover:underline mt-2">
                    Forgot your password?
                  </button>
                )}
              </div>
            )}

            {error && (
              <div className="border border-alert rounded-[2px] px-3 py-2">
                <p className="text-body text-alert">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-petrol text-white text-input py-3 rounded-[2px] hover:opacity-90 disabled:opacity-50"
            >
              {loading ? 'One moment…' : submitLabel}
            </button>
          </form>
          )}

          {mode === 'reset' ? (
            !resetSent && (
              <p className="text-body text-muted mt-6">
                Remembered it?{' '}
                <button onClick={() => goTo('login')} className="text-petrol hover:underline">Sign in</button>
              </p>
            )
          ) : (
            <p className="text-body text-muted mt-6">
              {mode === 'signup' ? 'Already set up? ' : 'No library yet? '}
              <button
                onClick={() => goTo(mode === 'signup' ? 'login' : 'signup')}
                className="text-petrol hover:underline"
              >
                {mode === 'signup' ? 'Sign in' : 'Request access'}
              </button>
            </p>
          )}

          <p className="text-label text-muted mt-10 border-t border-hairline pt-4">
            Teachers booking the library do not sign in here —{' '}
            <Link to="/calendar" className="text-petrol hover:underline">use the booking portal</Link>{' '}
            with your library card number and PIN.
          </p>
        </div>
      </main>
    </div>
  );
}


export default Gatekeeper;